import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { CircularProgress, Portal } from '@material-ui/core';
import { Dialog as EEComponentsDialog } from '@dev/ee-components';

import { ApplicationState as State } from '@/store';
import { HideDialog } from '@/store/dialog/actions';

import useMobile from '../hooks/useMobile';
import { DialogProps } from './DialogInterface';
import { useAvailableDialogsConfig } from './useAvailableDialogs';
import { DialogIds } from './DialogIds.enum';

/* eslint-disable react/no-unused-prop-types */
/* eslint-disable no-shadow */
interface StyledProps {
    $width: string | number;
    $height: string | number;
    isMobile: boolean;
    disableContentPadding?: boolean;
}

const StyledDialog = styled.div<StyledProps>`
    word-break: break-word;
    z-index: 1500;
    ${({ disableContentPadding }) =>
        disableContentPadding &&
        `
    .MuiDialogContent-root:first-child {
        padding: 0;
    }
    `}
    & .MuiDialog-root {
        z-index: 1301 !important;
    }

    & .MuiDialogTitle-root {
        & > .MuiTypography-root {
            padding-right: 10%;
            overflow: hidden;
            & > div {
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
    ${({ $width, $height, isMobile }): string => `
& .MuiDialog-paperWidthSm {
    max-width: ${isMobile ? '80vw' : $width};
    height: ${isMobile ? '80vh' : $height};
}`}
`;

const actions = { HideDialog };

interface DispatchProps {
    HideDialog(): void;
}

type Props = DialogProps & DispatchProps;

const Dialog: React.FC<DialogProps> = (props: Props): JSX.Element => {
    const dialogConfig = useAvailableDialogsConfig(props.id as DialogIds);
    const {
        HideDialog,
        isOpened,
        body,
        title,
        width,
        height,
        destructiveLabel,
        customButtons,
        onClickCancel,
        onClickConfirm,
        onClickDestructive,
        isHiddenManually,
        disableHeader,
        disableFooter,
        id,
        isLoading,
        header,
        disabledContentPadding,
        isDisabled,
        cancelLabel = 'Cancel',
        confirmLabel = 'Ok',
        onClose,
    }: Props = { ...props, ...dialogConfig };
    const isMobile = useMobile();

    const onConfirm = (): void => {
        if (onClickConfirm) {
            onClickConfirm();
        }
        if (!isHiddenManually) {
            HideDialog();
        }
    };

    const onCancel = (): void => {
        if (onClickCancel) {
            onClickCancel();
        }
        HideDialog();
    };

    const onDestructive = (): void => {
        if (onClickDestructive) {
            onClickDestructive();
        } else if (props.onClickDelete) {
            props.onClickDelete();
        }
        HideDialog();
    };

    const confirmLabelWithLoader = isLoading ? <CircularProgress color="secondary" size={16} /> : confirmLabel;

    return (
        <Portal>
            <StyledDialog $width={width} $height={height} isMobile={isMobile} disableContentPadding={disableHeader}>
                <EEComponentsDialog
                    disableHeader={disableHeader}
                    open={isOpened}
                    title={title}
                    body={body as JSX.Element}
                    onCancelClick={onCancel}
                    onConfirmClick={onConfirm}
                    onDestructiveClick={onDestructive}
                    cancelLabel={cancelLabel}
                    destructiveLabel={destructiveLabel ? destructiveLabel : props.deleteLabel}
                    confirmLabel={onClickConfirm ? (confirmLabelWithLoader as any) : null}
                    customButtons={customButtons as JSX.Element}
                    isDisabled={isDisabled}
                    disableFooter={disableFooter}
                    header={header}
                    disabledContentPadding={disabledContentPadding}
                    id={id as string}
                    onCloseClick={() => {
                        onClose?.();
                        HideDialog?.();
                    }}
                />
            </StyledDialog>
        </Portal>
    );
};

const mapStateToProps = (state: State, ownProps: DialogProps): DialogProps => {
    const { dialog } = state;
    return {
        ...ownProps,
        ...dialog,
    };
};

export default connect<DialogProps, DispatchProps>(mapStateToProps, actions)(Dialog);

export { Dialog as DialogTemplate };
